import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type ModulePermissions = {
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_moderate: boolean;
  can_manage_roles: boolean;
  can_manage_admins: boolean;
  can_block_user: boolean;
  can_edit_user_profile: boolean;
};

export type AdminPermissions = Record<string, ModulePermissions>;

const EMPTY_PERMS: ModulePermissions = {
  can_view: false,
  can_create: false,
  can_edit: false,
  can_delete: false,
  can_moderate: false,
  can_manage_roles: false,
  can_manage_admins: false,
  can_block_user: false,
  can_edit_user_profile: false,
};

export function useAdminPermissions() {
  const { user } = useAuth();

  const { data: permissions, isLoading } = useQuery({
    queryKey: ["admin-permissions", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_admin_permissions", {
        _user_id: user!.id,
      });
      if (error) throw error;
      return (data as AdminPermissions) ?? {};
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const can = (module: string, permission: keyof ModulePermissions): boolean => {
    if (!permissions) return false;
    return permissions[module]?.[permission] ?? false;
  };

  const canView = (module: string) => can(module, "can_view");
  const canEdit = (module: string) => can(module, "can_edit");
  const canCreate = (module: string) => can(module, "can_create");
  const canDelete = (module: string) => can(module, "can_delete");
  const canModerate = (module: string) => can(module, "can_moderate");

  const modulePerms = (module: string): ModulePermissions =>
    permissions?.[module] ?? EMPTY_PERMS;

  const hasAnyPermission = !!permissions && Object.keys(permissions).length > 0;

  return {
    permissions,
    isLoading,
    can,
    canView,
    canEdit,
    canCreate,
    canDelete,
    canModerate,
    modulePerms,
    hasAnyPermission,
  };
}
